import { ConfirmModal } from '@/components/ConfirmModal'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import { useDeleteDummyPerson, useGetDummyPersons } from '@/hooks/useDummyPersons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import {
  Box, Button, Flex, HStack, Icon, IconButton,
  Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useDisclosure, useToast,
} from '@chakra-ui/react'
import { faBriefcase, faBuilding, faCopy, faPlus, faTrash, faUser } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EmptyTablePlaceholder, TableSkeleton } from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums/Selection'
import { useState } from 'react'
import { useNavigate } from 'react-router'

const personTypeIcon = (type: Selection) => {
  if (type === Selection.Company) return faBuilding
  if (type === Selection.SelfEmployed) return faBriefcase
  return faUser
}

const personTypeLabel = (type: Selection) => {
  if (type === Selection.Company) return 'Právnická osoba'
  if (type === Selection.SelfEmployed) return 'OSVČ'
  return 'Fyzická osoba'
}

const getDisplayName = (p: PersonAdditionalData): string => {
  if (p.type === Selection.Company) return p.companyName ?? '–'
  return [p.titleBefore, p.name, p.surname, p.titleAfter].filter(Boolean).join(' ') || '–'
}

const getDisplayIdentifier = (p: PersonAdditionalData): string => {
  if (p.type === Selection.Company) return p.idendificationNumber ?? '–'
  if (p.type === Selection.SelfEmployed) return p.birthNumber + ' / ' + p.idendificationNumber
  return p.birthNumber ?? '–'
}

const getDisplayAddress = (p: PersonAdditionalData): string => {
  const a = p.address
  if (!a) return '–'
  const street = [a.street, a.streetNumber].filter(Boolean).join(' ')
  const parts = [street || null, a.postCode, a.city].filter(Boolean)
  return parts.join(', ') || '–'
}

export const DummyPersonsPage = () => {
  const { data: persons, isLoading, refetch } = useGetDummyPersons()
  const { mutateAsync: deletePerson } = useDeleteDummyPerson()
  const toast = useToast()
  const navigate = useNavigate()

  const confirmDisclosure = useDisclosure()
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const handleDeleteClick = (id: number) => {
    setDeleteId(id)
    confirmDisclosure.onOpen()
  }

  const handleDeleteConfirm = async (confirmed: boolean) => {
    confirmDisclosure.onClose()
    if (!confirmed || deleteId === null) return
    try {
      await deletePerson(deleteId)
      await refetch()
      toast({ title: 'Klient byl smazán', status: 'success', duration: 3000 })
    } catch {
      toast({ title: 'Nepodařilo se smazat klienta', status: 'error', duration: 4000 })
    }
  }

  return (
    <PageWrapper
      header={<DashboardHeader title="Správa dummy klientů" />}
      mainProps={{ sx: { pt: { base: 0, md: 8 }, gap: 6, display: 'grid' } }}
    >
      <Box bg="white" p={6} borderRadius="lg" shadow="sm">
        <Flex justify="flex-end" mb={4}>
          <Button
            colorScheme="primary"
            leftIcon={<Icon as={FontAwesomeIcon} icon={faPlus} />}
            onClick={() => navigate('/supervizor/dummy-persons/create')}
          >
            Přidat klienta
          </Button>
        </Flex>

        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Id</Th>
              <Th w="48px">Typ</Th>
              <Th>Jméno / Název firmy</Th>
              <Th>RČ / IČO</Th>
              <Th>Adresa</Th>
              <Th>Kontakt</Th>
              <Th w="80px" />
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <TableSkeleton colSpan={7} rowsCount={5} />
            ) : !persons?.length ? (
              <EmptyTablePlaceholder colSpan={7} />
            ) : persons.map(person => (
              <Tr
                key={person.id}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                onClick={() => navigate(`/supervizor/dummy-persons/edit/${person.id}`)}
              >
                <Td onClick={e => e.stopPropagation()}>
                  <HStack spacing={1}>
                    <CustomTooltip label="Kopírovat ID" placement="top">
                      <Flex
                        gap={1}
                        onClick={() => {
                          navigator.clipboard.writeText(String(person.id))
                          toast({
                            title: `Id klienta ${person.id} zkopírováno`,
                            status: 'success',
                            duration: 2000,
                          })
                        }}
                      >
                        <IconButton
                          aria-label="Kopírovat ID"
                          icon={<Icon as={FontAwesomeIcon} icon={faCopy} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                        />
                        <Text fontWeight="bold">{person.id}</Text>
                      </Flex>
                    </CustomTooltip>
                  </HStack>
                </Td>
                <Td>
                  <Tooltip label={personTypeLabel(person.type)} placement="right">
                    <Icon
                      as={FontAwesomeIcon}
                      icon={personTypeIcon(person.type)}
                      color="primary.500"
                      fontSize="lg"
                    />
                  </Tooltip>
                </Td>
                <Td>
                  <Text fontWeight="medium">{getDisplayName(person)}</Text>
                </Td>
                <Td >
                  <Text fontSize="xs" >{getDisplayIdentifier(person)}</Text>
                </Td>
                <Td fontSize="sm">{getDisplayAddress(person)}</Td>
                <Td fontSize="sm">
                  <Stack spacing={0}>
                    {person.phone && <Text>{person.phone}</Text>}
                    {person.email && <Text>{person.email}</Text>}
                    {!person.phone && !person.email && '–'}
                  </Stack>
                </Td>
                <Td onClick={e => e.stopPropagation()}>
                  <HStack justify="flex-end" spacing={1}>
                    <IconButton
                      aria-label="Smazat"
                      p={2}
                      icon={<Icon as={FontAwesomeIcon} icon={faTrash} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDeleteClick(person.id!)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <ConfirmModal
        isOpen={confirmDisclosure.isOpen}
        onClose={handleDeleteConfirm}
        title="Smazat dummy klienta"
        yesButtonContent="Smazat"
        noButtonContent="Zrušit"
      >
        Opravdu chcete smazat tohoto dummy klienta? Tato akce je nevratná.
      </ConfirmModal>
    </PageWrapper>
  )
}

